import React from 'react';
import { Paper } from '@mui/material';
import { Typography } from '@vibTheme/components/_base/Typography';
import {
  BOT_FUNNEL_STAGES,
  BOT_FUNNEL_STAGES_WITHOUT_DROP_OFF,
  BOT_FUNNEL_STAGE_HINTS,
  BOT_FUNNEL_STAGE_LABELS,
  BotFunnelOfferRow,
  BotFunnelResponse,
  BotFunnelStage,
} from '@interfaces/reports/botFunnel';
import { CustomHelpTooltip } from '../CustomHelpTooltip';
import { formatRate as formatRateWithDecimals } from '../reportFormat';
import { parseFunnelPaymentMethod } from '@utils/parseFunnelPaymentMethod';

export const HeadlineCard = ({
  title,
  value,
  caption,
}: {
  title: string;
  value: string;
  caption: string;
}) => (
  <div className="flex flex-col shadow-default rounded-default p-md">
    <Typography className="text-gray-400">{title}</Typography>
    <Typography className="text-[#374151] text-3xl font-bold">
      {value}
    </Typography>
    <Typography className="text-xs text-lightBlack">{caption}</Typography>
  </div>
);

/**
 * The explanation of a stage whose name does not carry it, and nothing at all for the ones that do.
 *
 * Inline next to the label rather than in a legend: the two stages that need it sound like each other,
 * so the distinction has to be reachable at the moment someone reads the two numbers side by side.
 */
const StageHint = ({ stage }: { stage: BotFunnelStage }) => {
  const hint = BOT_FUNNEL_STAGE_HINTS[stage];
  if (!hint) return null;

  return (
    <CustomHelpTooltip
      description={
        <Typography className="text-white text-xs p-sm">{hint}</Typography>
      }
    />
  );
};

/**
 * The drop-off, in funnel order.
 *
 * Bars are sized against the TOP stage - conversations started - so the shape of the funnel is readable
 * at a glance, and the stage-to-stage number is shown next to it because that is the one that answers
 * where the loss is. Neither number is capped at 100%.
 *
 * Sized against START before, which drew a funnel wider at the bottom than at the top: most leads
 * arrive without ever sending /start, so nearly every bar overflowed and clamped to full width.
 */
export const StageBreakdown = ({
  stages,
}: {
  stages: BotFunnelResponse['stages'];
}) => {
  const byStage = new Map(stages.map((stage) => [stage.stage, stage]));
  const topLeads = byStage.get('ATTEMPT_OPENED')?.leads ?? 0;

  return (
    <Paper className="mt-main p-main">
      <Typography component="h3" className="pb-sm">
        Onde as pessoas param
      </Typography>
      <div className="grid gap-sm">
        {BOT_FUNNEL_STAGES.map((stage: BotFunnelStage, index) => {
          const row = byStage.get(stage);
          const leads = row?.leads ?? 0;
          const width = topLeads ? (leads / topLeads) * 100 : 0;
          const previous = index === 0 ? null : BOT_FUNNEL_STAGES[index - 1];
          const previousLeads = previous
            ? byStage.get(previous)?.leads ?? 0
            : 0;
          const lost = previous ? previousLeads - leads : 0;
          // A etapa continua visível com seu número; só não recebe linha de perda. Ver
          // BOT_FUNNEL_STAGES_WITHOUT_DROP_OFF: /start é subconjunto de quem iniciou conversa, então a
          // subtração contra a etapa de cima é sempre positiva e leria como desistência.
          const carriesDropOff =
            !BOT_FUNNEL_STAGES_WITHOUT_DROP_OFF.includes(stage);

          return (
            <div key={stage} className="grid gap-[2px]">
              <div className="flex justify-between items-baseline gap-sm">
                <Typography className="text-sm">
                  {BOT_FUNNEL_STAGE_LABELS[stage]}
                  <StageHint stage={stage} />
                </Typography>
                <Typography className="text-sm whitespace-nowrap">
                  <strong>{leads}</strong>
                  <span className="text-lightBlack">
                    {' '}
                    ({((row?.rateFromTop ?? 0) * 100).toFixed(1)}% do topo)
                  </span>
                </Typography>
              </div>
              <div className="h-[10px] w-full bg-[#f3f4f6] rounded-[6px] overflow-hidden">
                <div
                  className="h-full bg-primary rounded-[6px]"
                  style={{ width: `${Math.min(width, 100)}%` }}
                />
              </div>
              {previous && carriesDropOff && (
                <Typography className="text-xs text-lightBlack">
                  {describeStageDelta(lost, row?.rateFromPrevious ?? 0)}
                </Typography>
              )}
              <Typography className="text-xs text-lightBlack">
                {row?.attempts ?? 0}{' '}
                {(row?.attempts ?? 0) === 1 ? 'tentativa' : 'tentativas'}
              </Typography>
            </div>
          );
        })}
      </div>
    </Paper>
  );
};

/**
 * Per campaign the lead arrived through, on the SAME top as the funnel above it.
 *
 * It used to count from /start alone, so the table carried a tooltip apologising for numbers that were not
 * comparable with the funnel. Vibxtech/report-api#61 removed that limitation and the apology outlived it -
 * which is worse than saying nothing, because it teaches the producer to distrust a number that has become
 * trustworthy.
 *
 * /start stays as a secondary column rather than being dropped: it is still a real question about how people
 * arrive, and on a campaign that is entirely deep links it is the number that explains why the two differ.
 * It is just no longer the denominator of anything here.
 *
 * The one true half of the old tooltip is kept: these rows still do not SUM to the funnel's own lead count,
 * because someone who arrives through two campaigns is one lead in the headline and one lead in each row.
 * That is a property of distinct counts, not a measurement gap, and no widening of the top can fix it.
 */
export const OriginBreakdown = ({ data }: { data: BotFunnelResponse }) => (
  <Paper className="mt-main p-main">
    <div className="flex items-center gap-sm pb-sm">
      <Typography component="h3">Por origem do link</Typography>
      <CustomHelpTooltip
        description={
          <div className="grid gap-sm p-sm">
            <Typography className="text-white text-xs">
              Conta o <strong>mesmo topo do funil</strong> acima — quem iniciou
              uma conversa —, então a taxa desta tabela é comparável com a
              conversão do funil.
            </Typography>
            <Typography className="text-white text-xs">
              <strong>Não some as linhas.</strong> Quem chegou por duas
              campanhas conta uma vez em cada uma e uma vez só no funil, então o
              total daqui fica maior que o de lá sem que nada esteja errado.
            </Typography>
            <Typography className="text-white text-xs">
              <strong>/start</strong> é um subconjunto dos leads: quem chega por
              link direto, oferta divulgada ou pedido de entrada em grupo nunca
              envia o comando. Numa campanha que é toda link direto, essa coluna
              fica perto de zero e não indica problema.
            </Typography>
          </div>
        }
      />
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-lightBlack">
            <th className="py-sm pr-main font-normal">Origem</th>
            <th className="py-sm pr-main font-normal">Leads</th>
            <th className="py-sm pr-main font-normal">Leads com /start</th>
            <th className="py-sm pr-main font-normal">Compradores</th>
            <th className="py-sm pr-main font-normal">Vendas pagas</th>
            <th className="py-sm font-normal">Compradores / leads</th>
          </tr>
        </thead>
        <tbody>
          {data.byOrigin.map((row) => (
            <tr key={row.origin} className="border-t border-[#f3f4f6]">
              <td className="py-sm pr-main">{row.origin}</td>
              <td className="py-sm pr-main">{row.leads}</td>
              <td className="py-sm pr-main">{row.starts}</td>
              <td className="py-sm pr-main">{row.purchasers}</td>
              <td className="py-sm pr-main">{row.paidSales}</td>
              <td className="py-sm font-bold">{formatRate(row.purchaseRate)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Paper>
);

/**
 * Per offer, impression to sale.
 *
 * Two rates rather than one, because they point at different problems. A low "escolheram" means the offer
 * does not attract - wrong price, wrong copy, wrong position in the list. A healthy "escolheram" with a low
 * "compraram" means it attracts and then loses people further down, in the email, the document or the
 * charge itself. One combined number would hide which of the two is happening.
 */
export const OfferBreakdown = ({ rows }: { rows: BotFunnelOfferRow[] }) => (
  <Paper className="mt-main p-main">
    <div className="flex items-center gap-sm pb-sm">
      <Typography component="h3">Por oferta</Typography>
      <CustomHelpTooltip
        description={
          <div className="grid gap-sm p-sm">
            <Typography className="text-white text-xs">
              <strong>Viram</strong> conta pessoas distintas a quem a oferta foi
              exibida no bot. É o denominador que faltava: no relatório de
              ofertas, uma oferta que ninguém compra parece igual a uma que
              ninguém vê.
            </Typography>
            <Typography className="text-white text-xs">
              <strong>Escolheram baixo</strong> = a oferta não atrai.{' '}
              <strong>Escolheram alto e vendas baixas</strong> = ela atrai e
              perde a pessoa depois, no cadastro ou no pagamento.
            </Typography>
            <Typography className="text-white text-xs">
              <strong>Vendas</strong> conta vendas, não pessoas: quem comprou a
              mesma oferta duas vezes é uma pessoa em &quot;Viram&quot; e duas
              aqui. Por isso <strong>Vendas / viram</strong> pode passar de 100%
              sem que nada esteja errado.
            </Typography>
          </div>
        }
      />
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-lightBlack">
            <th className="py-sm pr-main font-normal">Oferta</th>
            <th className="py-sm pr-main font-normal">Viram</th>
            <th className="py-sm pr-main font-normal">Escolheram</th>
            <th className="py-sm pr-main font-normal">Vendas</th>
            <th className="py-sm pr-main font-normal">% escolheram</th>
            <th className="py-sm font-normal">Vendas / viram</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.offerId ?? 'no-offer'}
              className="border-t border-[#f3f4f6]"
            >
              <td className="py-sm pr-main">{describeOffer(row)}</td>
              <td className="py-sm pr-main">{row.impressions}</td>
              <td className="py-sm pr-main">{row.selected}</td>
              <td className="py-sm pr-main">{row.paidSales}</td>
              <td className="py-sm pr-main">{formatRate(row.selectionRate)}</td>
              <td className="py-sm font-bold">
                {formatRate(row.impressionToSaleRate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Paper>
);

/**
 * An offer deleted since the impression has no name, and the row still matters - its impressions are in
 * the denominator. Showing the id keeps it identifiable instead of blank.
 */
const describeOffer = (row: BotFunnelOfferRow) => {
  if (row.offerName) return row.offerName;
  if (row.offerId === null) return '(sem oferta)';

  return `Oferta #${row.offerId} (removida)`;
};

/**
 * Per payment method the lead chose in the bot.
 *
 * Deliberately not the same question as the payment card on this screen. That one measures charges that
 * were GENERATED; this one starts one step earlier, at the choice, so the lead who picked PIX and never
 * reached a charge appears here and nowhere else.
 */
export const PaymentMethodFunnelBreakdown = ({
  rows,
}: {
  rows: BotFunnelResponse['byPaymentMethod'];
}) => (
  <Paper className="mt-main p-main">
    <div className="flex items-center gap-sm pb-sm">
      <Typography component="h3">Por método escolhido no bot</Typography>
      <CustomHelpTooltip
        description={
          <div className="grid gap-sm p-sm">
            <Typography className="text-white text-xs">
              Conta pela <strong>escolha do método</strong>, não pela cobrança
              gerada. Quem escolheu PIX e desistiu antes de a cobrança existir
              aparece aqui — e em nenhum outro lugar.
            </Typography>
            <Typography className="text-white text-xs">
              Por isso estes números não batem com o bloco &quot;Pagamento das
              cobranças&quot; acima, que parte da cobrança já gerada.
            </Typography>
            <Typography className="text-white text-xs">
              <strong>Escolheram</strong> conta pessoas e{' '}
              <strong>Vendas</strong> conta vendas, então a razão entre os dois
              pode passar de 100% — quem escolheu PIX uma vez e comprou duas
              aparece como uma pessoa e duas vendas.
            </Typography>
          </div>
        }
      />
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-lightBlack">
            <th className="py-sm pr-main font-normal">Método</th>
            <th className="py-sm pr-main font-normal">Escolheram</th>
            <th className="py-sm pr-main font-normal">Vendas</th>
            <th className="py-sm font-normal">Vendas / escolheram</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.paymentMethod} className="border-t border-[#f3f4f6]">
              <td className="py-sm pr-main">
                {parseFunnelPaymentMethod(row.paymentMethod)}
              </td>
              <td className="py-sm pr-main">{row.selected}</td>
              <td className="py-sm pr-main">{row.paidSales}</td>
              <td className="py-sm font-bold">
                {formatRate(row.selectionToSaleRate)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </Paper>
);

/**
 * Duas casas, porque aqui as taxas são sobre impressão e sobre escolha de método.
 *
 * Numa comunidade pequena elas ficam abaixo de 1% com frequência, e arredondar para uma casa apagaria a
 * diferença entre "quase ninguém" e "ninguém". A implementação vem de reportFormat: a versão que morava
 * aqui não tinha guarda de finito, então um campo ausente na resposta imprimia "NaN%" no meio da tabela.
 */
export const formatRate = (rate: number) => formatRateWithDecimals(rate, 2);

/**
 * Explicit empty state instead of a funnel full of zeros.
 *
 * Collection only starts once the instrumentation flag is switched on in the bot, so any period before
 * that legitimately has no data. Rendering "0%" there would tell the producer their conversion is zero.
 */
export const EmptyFunnel = ({
  collectionStartedAt,
}: {
  collectionStartedAt: string | null;
}) => (
  <Paper className="p-main">
    <Typography className="text-sm">
      Ainda não há dados de funil para o período e as comunidades selecionadas.
    </Typography>
    <Typography className="text-sm text-lightBlack pt-sm">
      {collectionStartedAt
        ? `A medição começou em ${formatDay(
            collectionStartedAt,
          )}. Períodos anteriores a essa data aparecem vazios: não significa conversão zero, significa que não havia medição.`
        : 'A medição das etapas do bot ainda não registrou nada para estas comunidades. Não significa conversão zero, significa que não havia medição.'}
    </Typography>
  </Paper>
);

/**
 * The case that actually misleads.
 *
 * The dashboard defaults to the last 30 days while measurement is days old, so a producer lands on a
 * funnel built from a fraction of the period, right next to sales cards covering all of it. They compare
 * "vendi 40 no mês" with "o funil diz 3 vendas" and either distrust the whole page or believe the funnel.
 * Neither is acceptable, and silence is what causes it.
 */
export const PartialCoverageWarning = ({
  startedAt,
  uncoveredDays,
}: {
  startedAt: string;
  uncoveredDays: number;
}) => (
  <Paper className="p-main mb-main border-l-4 border-l-[#f59e0b]">
    <Typography className="text-sm">
      <strong>Período parcialmente medido.</strong> A medição começou em{' '}
      {formatDay(startedAt)}, então {describeUncovered(uncoveredDays)} do
      período selecionado não têm dados.
    </Typography>
    <Typography className="text-sm text-lightBlack pt-sm">
      Os números abaixo cobrem só a parte medida, e por isso ficam menores que
      os cards de venda, que cobrem o período inteiro. As taxas continuam
      válidas — elas comparam etapas dentro da mesma janela medida.
    </Typography>
  </Paper>
);

export const formatDay = (iso: string) => {
  const date = new Date(iso);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString('pt-BR')
    : iso;
};

/**
 * Sentence under each bar.
 *
 * The alternative-path wording is reserved for a stage that actually EXCEEDS its predecessor. Treating
 * "did not lose anyone" as an alternative path meant two stages with the same count - including two
 * empty ones, which read "0% ... caminho alternativo" - claimed a behaviour that had not happened.
 */
export const describeStageDelta = (lost: number, rateFromPrevious: number) => {
  const rate = `${(rateFromPrevious * 100).toFixed(1)}%`;

  if (lost > 0)
    return `perdeu ${lost} em relação à etapa anterior (${rate} seguiram)`;
  if (lost === 0)
    return `não perdeu ninguém em relação à etapa anterior (${rate})`;

  return `${rate} da etapa anterior — mais gente que na etapa anterior, caminho alternativo, veja a ajuda acima`;
};

/**
 * uncoveredDays is 0 when measurement began later on the first day of the period - real gap, shorter
 * than a day. Naming "0 dias" would read as no gap at all.
 */
export const describeUncovered = (uncoveredDays: number) => {
  if (uncoveredDays <= 0) return 'as primeiras horas';
  if (uncoveredDays === 1) return 'o primeiro dia';

  return `os ${uncoveredDays} primeiros dias`;
};

/** Whole number when it is one, one decimal otherwise: "1 : 87" reads better than "1 : 87.0". */
export const formatSalesRatio = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toFixed(1);

/** The three caveats a producer needs before reading any of these numbers. */
export const FunnelHelpTooltip = () => (
  <CustomHelpTooltip
    description={
      <div className="grid gap-sm p-sm">
        <Typography className="text-white text-xs">
          <strong>Só o bot.</strong> Venda feita direto no checkout da web só
          entra aqui se o comprador demonstrou intenção de compra no bot antes.
          Se você vende muito fora do bot, esta taxa vai ser baixa — e está
          correta.
        </Typography>
        <Typography className="text-white text-xs">
          <strong>As etapas contam pessoas.</strong> Quem voltou depois de 6
          horas e comprou na segunda vez conta como uma pessoa em cada etapa —
          as conversas aparecem separadas, abaixo de cada etapa e no card do
          topo.
        </Typography>
        <Typography className="text-white text-xs">
          <strong>Etapa acima da anterior não é erro.</strong> Quem chega por
          uma oferta promovida pula o menu de categorias, então "Viu as ofertas"
          pode ter mais gente que "Viu o menu".
        </Typography>
      </div>
    }
  />
);
